import { Avatar, Box, TextField, Typography, useTheme } from '@mui/material';
import React, { Fragment, useEffect, useState, type ChangeEvent } from 'react';
import { colorTheme } from 'theme';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useParams } from 'react-router-dom';
import { updatePassword, logout, logoutTokenError } from 'service/AuthService';
import { getUserTokenById } from 'service/UserService';
import { useDispatch } from 'react-redux';
import { setSnackBarSuccess, setSnackBarFailed } from 'store/snackBar';
import LoadingButton from '@mui/lab/LoadingButton';
import { strings } from 'i18n/Strings';
import Logo from '../../../assets/images/Logo100X132.png';
interface IUpdatePasswordData {
  password: string;
  confirmPassword: string;
}

interface IEmployeeData {
  basicInfo: { employeeResetToken: string; organizationId: number };
  message: string;
}

const CreatePassword = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfrimPassword, setShowConfrimPassword] =
    useState<boolean>(false);
  const [userVisit, setUserVist] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [matchError, setMatchError] = useState<string>('');
  const [tokenCheck, setTokenCheck] = useState<string>('');
  const navigate = useNavigate();
  const { empId, token } = useParams<Record<string, string | undefined>>();
  const [loader, setLoader] = useState<boolean>(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [expiredError, setExpiredError] = useState('');

  const userTokenCheck = async (): Promise<void> => {
    if (empId) {
      try {
        const res = (await getUserTokenById(Number(empId))) as IEmployeeData;
        if (res.basicInfo?.employeeResetToken) {
          setTokenCheck(res.basicInfo?.employeeResetToken);
        }
      } catch (err) {
        throw new Error(`${String(err)}`);
      }
    }
  };

  useEffect(() => {
    setTokenCheck('');
    void userTokenCheck();
  }, []);

  if (tokenCheck && token !== tokenCheck) {
    logoutTokenError();
  }

  const handleErrorCheck = (): void => {
    if (inputDisabled) {
      setErrorMsg('');
      setMatchError('');
      return;
    }
    if (password !== '') {
      if (password.length < 9) {
        setErrorMsg(strings.displayText.passwordMustBeNineErrText);
      } else if (
        password.match(/[0-9]/) != null &&
        password.match(/[A-Z]/) != null &&
        password.match(/[a-z]/) != null &&
        password.match(/^(?=.*[~`!@#$%^&*()--+={}\\[\]|\\:;"'<>,.?/_₹]).*$/) !=
          null
      ) {
        setErrorMsg('');
      } else {
        setErrorMsg(strings.displayText.passwordRegexValidationText);
      }
    } else if (userVisit) {
      setErrorMsg(strings.displayText.passwordMustValidationText);
    } else {
      setErrorMsg('');
    }
    // match paassword check
    if (password !== '') {
      if (confirmPassword.length > 0 && confirmPassword !== password) {
        setMatchError(strings.displayText.passwordsDoNotMatch);
      } else {
        setMatchError('');
      }
    } else {
      setMatchError('');
    }
  };

  useEffect(() => {
    handleErrorCheck();
  }, [password, userVisit, confirmPassword]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const data: IUpdatePasswordData = {
      password,
      confirmPassword,
    };
    if (confirmPassword !== '') {
      if (password === confirmPassword) {
        setMatchError('');
      } else {
        setMatchError(strings.displayText.passwordsDoNotMatch);
      }
    } else {
      setMatchError(strings.displayText.confirmYourPassword);
    }

    if (
      !errorMsg &&
      !matchError &&
      password === confirmPassword &&
      empId != null &&
      token != null
    ) {
      void (async () => {
        setLoader(true);

        await updatePassword(empId, token, data)
          .then(async (res: unknown) => {
            const myRes = res as IEmployeeData;
            setLoader(false);
            await logout();
            if (myRes?.message === strings.displayText.invalidSignatureText) {
              dispatch(setSnackBarFailed({ snackBarMessage: myRes?.message }));
              navigate('/tokenerror');
            } else {
              dispatch(
                setSnackBarSuccess({
                  snackBarMessage: strings.displayText.passwordUpdateSucessText,
                }),
              );

              navigate('/');
            }
          })
          .catch((err: unknown) => {
            setLoader(false);
            console.log('error', err);
            setPassword('');
            setConfirmPassword('');
            setExpiredError(strings.displayText.expiredResetLinkError);
            setInputDisabled(true);
          });
      })();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
  };

  const handleClickShowPassword = (): void => {
    setShowPassword((show) => !show);
  };
  const handleShowConfrimPassword = (): void => {
    setShowConfrimPassword((show) => !show);
  };

  const styles = {
    mainBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: 350,
      marginX: 'auto',
    },
    avatarStyle: {
      width: 100,
      height: 100,
      p: 2,
      bgcolor: colors.primaryBg[900],
      boxShadow: 3,
      position: 'relative',
      top: -48,
    },
    setPasswrdTypho: {
      fontFamily: 'Source Sans Pro !important',
      fontWeight: 700,
      color: colors.greenAccent[500],
    },
    createPswrdInfo: {
      textAlign: 'center',
      color: colors.gray[500],
      fontWeight: 400,
      fontSize: 14,
    },
    formBox: {
      mt: 1,
    },
    passwrdTextField: {
      input: {
        '&:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 100px #eef9fa inset',
          '-webkit-text-fill-color': colors.greenAccent[100],
        },
      },
    },
    errMsg: {
      position: errorMsg.length > 38 ? '' : 'absolute',
      fontWeight: 400,
      fontSize: 14,
      color: colors.primaryRed[100],
    },
    expiredError: {
      fontWeight: 400,
      fontSize: 12,
      color: colors.primaryRed[100],
      textAlign: 'center',
      marginTop: 1.5,
    },
    cnfmPswrdTypho: {
      marginTop: errorMsg.length > 38 ? 1 : 5,
      input: {
        '&:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 100px #eef9fa inset',
          '-webkit-text-fill-color': colors.greenAccent[100],
        },
      },
    },
    matchErr: {
      position: 'absolute',
      fontWeight: 400,
      fontSize: 14,
      color: colors.primaryRed[100],
    },
    lodeBtn: {
      mt: 3,
      mb: 2,
      bgcolor: colors.greenAccent[500],
      borderRadius: 2,
      py: 1.5,
      marginTop: 5,
    },
  };
  return (
    <Fragment>
      <Box sx={styles.mainBox}>
        <Avatar alt="Zen Pensions logo" sx={styles.avatarStyle}>
          <img width={46} height={56} src={Logo} />
        </Avatar>
        <Typography component="h1" variant="h2" sx={styles.setPasswrdTypho}>
          {strings.displayText.setYourPassword}
        </Typography>
        <Box pb={1} />
        <Typography component="h5" variant="h5" sx={styles.createPswrdInfo}>
          {strings.displayText.passwrdContainMsg}
        </Typography>
        <Box pb={2} />
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={styles.formBox}
        >
          <TextField
            disabled={inputDisabled}
            margin="normal"
            required
            fullWidth
            id="password"
            label="Enter Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="*****"
            autoComplete="password"
            autoFocus
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleChange(e);
            }}
            sx={styles.passwrdTextField}
            onBlur={() => {
              setUserVist(true);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography component="h5" variant="h5" sx={styles.errMsg}>
            {errorMsg}
          </Typography>
          <TextField
            disabled={inputDisabled}
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type={showConfrimPassword ? 'text' : 'password'}
            id="confirmPassword"
            placeholder="*****"
            autoComplete="current-password"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={(e: any) => {
              setConfirmPassword(e.target.value);
            }}
            sx={styles.cnfmPswrdTypho}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowConfrimPassword}>
                    {showConfrimPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography component="h5" variant="h5" sx={styles.matchErr}>
            {matchError}
          </Typography>
          <Typography component="h5" variant="h5" sx={styles.expiredError}>
            {expiredError}
          </Typography>

          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loading={loader}
            disabled={inputDisabled}
            loadingPosition="end"
            onClick={() => {
              handleErrorCheck();
            }}
            sx={styles.lodeBtn}
          >
            <Typography component="h1" variant="h5">
              {strings.displayText.continueText}
            </Typography>
          </LoadingButton>
        </Box>
      </Box>
    </Fragment>
  );
};
export default CreatePassword;
